"use client";

import * as styles from "./styles.css";
import { stegaClean } from "@sanity/client/stega";
import PageSection from "@/components/page-section";
import ContentContainer from "@/components/content-container";
import Icon from "@/elements/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import HeadingAndText from "@/components/heading-and-text";
import Accordion from "@/components/accordion";
import { useSearchParams } from "next/navigation";

interface Props {
  sourceListData: any;
}

export default function FaqSourceTabs(props: Props) {
  const [activeId, setActiveId] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonItemContainerRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const category = searchParams.get("category");
  const [firstLoad, setFirstLoad] = useState(true);

  const checkIfOverflow = () => {
    if (containerRef.current && buttonItemContainerRef.current) {
      const mainWidth = containerRef.current.offsetWidth;
      const buttonContainerChild = buttonItemContainerRef.current.children;
      let buttonContainerWidth = 0;

      for (let i = 0; i < buttonContainerChild.length; i += 1) {
        const child = buttonContainerChild[i] as HTMLElement;
        buttonContainerWidth += child.offsetWidth;
      }

      if (mainWidth >= buttonContainerWidth) {
        setIsOverflow(false);
      } else {
        setIsOverflow(true);
      }
    }
  };

  const categories = useMemo(() => {
    if (props.sourceListData) {
      const items = stegaClean(props.sourceListData).map((d: any) =>
        d.headingText.toLowerCase().replace(/[^A-Z0-9]/gi, "-")
      );

      return items;
    }

    return [];
  }, [props.sourceListData]);

  const scrollToRow = (selector: string) => {
    const el = document.querySelector<HTMLElement>(selector);
    buttonItemContainerRef.current?.scrollTo({
      left: el?.offsetLeft,
      behavior: "smooth",
    });
  };

  const buttonNavHandler = (type: any) => {
    if (type === "next" && activeId + 1 <= categories.length - 1) {
      tabHandler(activeId + 1);
      scrollToRow(`#${stegaClean(categories[activeId + 1])}`);
    } else if (type === "prev" && activeId - 1 >= 0) {
      tabHandler(activeId - 1);
      scrollToRow(`#${stegaClean(categories[activeId - 1])}`);
    }
  };

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  const tabHandler = (id: number) => {
    window.history.pushState(
      null,
      "",
      `?${createQueryString("category", categories[id])}`
    );
  };

  const setCategoryIndex = () => {
    if (category) {
      const id = categories.indexOf(category);

      if (id !== -1) {
        setActiveId(id);

        if (firstLoad) {
          scrollToRow(`#${stegaClean(categories[id])}`);
        }
      }
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    if (category) {
      setCategoryIndex();
    }
  }, [category]);

  useEffect(() => {
    checkIfOverflow();
    window.addEventListener("resize", debounce(checkIfOverflow, 300));
  }, []);

  return (
    <PageSection>
      <ContentContainer layout="source">
        <div
          ref={containerRef}
          className={`${styles.container} is--module is--articlemodule--article-tabs `}
        >
          <div className={styles.buttonContainer}>
            <div
              className={`${styles.buttonNavContainer}${isOverflow ? " show" : ""} is--prev`}
            >
              <div className={`${styles.buttonIndicator} is--prev`}>
                <button
                  className={styles.iconContainer}
                  onClick={() => buttonNavHandler("prev")}
                  disabled={activeId - 1 < 0}
                >
                  <Icon type="chevron-left-unfilled" />
                </button>
              </div>
            </div>
            <div
              className={`${styles.buttonNavContainer}${isOverflow ? " show" : ""} is--next`}
            >
              <div className={`${styles.buttonIndicator} is--next`}>
                <button
                  className={styles.iconContainer}
                  onClick={() => buttonNavHandler("next")}
                  disabled={activeId + 1 > categories.length - 1}
                >
                  <Icon type="chevron-right-unfilled" />
                </button>
              </div>
            </div>
            <div
              ref={buttonItemContainerRef}
              className={styles.buttonItemContainer}
            >
              {categories?.map((item: any, index: number) => (
                <button
                  key={`${item}-${index}`}
                  className={`${stegaClean(categories[activeId]) === stegaClean(item) ? "is--active" : "is--normal"}`}
                  onClick={() => tabHandler(index)}
                  id={stegaClean(item)}
                >
                  {props.sourceListData[index].headingText}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.listContainer}>
            {props.sourceListData?.map((item: any, index: number) => (
              <div
                key={`item-list-${index}`}
                id={stegaClean(item.headingText)
                  .toLowerCase()
                  .replace(/[^A-Z0-9]/gi, "-")}
                className={`${styles.wrapper}${
                  stegaClean(item.headingText)
                    .toLowerCase()
                    .replace(/[^A-Z0-9]/gi, "-") ===
                  stegaClean(categories[activeId])
                    ? " is--active"
                    : " is--hidden"
                }`}
              >
                {stegaClean(item.headingText)
                  .toLowerCase()
                  .replace(/[^A-Z0-9]/gi, "-") ===
                  stegaClean(categories[activeId]) && (
                  <div className={styles.faqContainer}>
                    <div className={styles.textContainer}>
                      <HeadingAndText
                        headingText={item.headingText ?? ""}
                        bodyText={item.bodyText}
                        textAlign="left"
                      />
                    </div>
                    <div className={styles.contentContainer}>
                      <Accordion
                        listData={item.faqItems}
                        mode="single"
                        variant="small"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </ContentContainer>
    </PageSection>
  );
}
